import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare, CheckCircle, Trash2 } from 'lucide-react'
import { colors } from '@/styles/colors'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/store'

import { useTranslation } from '@/i18n'
import CommentThreadPopover from './CommentThreadPopover'

/**
 * Pointer travel (px) still counted as a click rather than a drag. A press that
 * moves further than this was a move, and must not also open the thread.
 */
const DRAG_SLOP = 4


interface CommentPinProps {
  commentId?: string
  resolved?: boolean
  /** Persisted shape prop; no longer rendered. It used to draw a small blue
   *  dot at the pin's lower left to mean "this thread has replies", which on a
   *  round pin floated in empty canvas and read as a stray dot (UI-57). The
   *  thread itself shows the replies. */
  hasReplies?: boolean
  unreadCount?: number
  /** Persisted shape prop; no longer rendered — the pin keeps its comment
   *  icon on hover (FS-581). The value was the raw user_id anyway, so the
   *  old hover overlay showed UUID first-chars like "6". */
  authorName?: string
  x: number
  y: number
}

const CommentPin = ({
  commentId,
  resolved = false,
  unreadCount = 0,
  x,
  y,
}: CommentPinProps) => {
  const { t } = useTranslation()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [screenPosition, setScreenPosition] = useState<{ x: number; y: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const pinRef = useRef<HTMLDivElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const { user, deleteComment, comments, board } = useAppStore()

  // UI-57 follow-up: the anchor is read when the thread OPENS, not when the
  // pin's page coordinates last changed. The old effect keyed on [x, y] missed
  // every camera pan and zoom (which move the pin on screen without touching
  // x/y), so after scrolling the board the popover opened where the pin USED
  // to be — often above it, with the header off the top of the viewport.
  const readAnchor = useCallback(() => {
    const rect = pinRef.current?.getBoundingClientRect()
    if (!rect) return
    setScreenPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    })
  }, [])

  // While the thread is open, a pin whose page coordinates change (a reconcile
  // moved it) keeps its popover beside it.
  useEffect(() => {
    if (isPopoverOpen) readAnchor()
  }, [x, y, isPopoverOpen, readAnchor])

  // Find the comment to check ownership
  const workspaceId = board?.id
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing recursive comment shape
  const findCommentById = (comms: any[], id: string): any => {
    for (const c of comms) {
      if (c.id === id) return c
      if (c.replies?.length > 0) {
        const found = findCommentById(c.replies, id)
        if (found) return found
      }
    }
    return null
  }
  const comment = workspaceId && commentId
    ? findCommentById(comments[workspaceId] || [], commentId)
    : null
  const isOwnComment = comment && user && comment.user_id === user.userId

  // UI-57 (Jenna): "No option to move comments."
  //
  // This used to be `onPointerDown={handlePinClick}` with stopPropagation and
  // preventDefault, which meant tldraw never saw the pointerdown at all — and a
  // translate only begins once the shape's pointerdown reaches the editor. The
  // pin was not locked or marked immovable anywhere; it was simply unreachable,
  // so no amount of dragging did anything.
  //
  // The pointerdown now passes through, and the popover opens on pointerUP
  // instead — but only when the pointer did not travel, otherwise every drag
  // would end by opening the thread it just moved. DRAG_SLOP absorbs the couple
  // of pixels a normal click carries; anything beyond that was a move.
  // The release is watched on WINDOW, not on the pin. Letting the press reach
  // tldraw is what makes the pin draggable, but tldraw answers it with
  // setPointerCapture on its own container — which retargets every later
  // pointer event for that gesture, so an onPointerUp bound to this element
  // never fires and the thread never opened. window still sees it.
  // Keyed by pointerId (graphite review): a single ref was overwritten when a
  // second finger pressed before the first lifted, so the first pointer's
  // teardown was lost — its window listener outlived the gesture and the
  // second pointer's was removed in its place.
  const releasesRef = useRef(new Map<number, () => void>())

  // Whatever is still armed at unmount is torn down with the component.
  useEffect(() => {
    const releases = releasesRef.current
    return () => {
      for (const teardown of [...releases.values()]) teardown()
      releases.clear()
    }
  }, [])

  const handlePinPointerDown = (e: React.PointerEvent) => {
    // Right-click is the context menu's business (handleContextMenu), and must
    // not arm a popover-opening press.
    if (e.button !== 0 || !commentId) return

    const startX = e.clientX
    const startY = e.clientY
    const pointerId = e.pointerId

    const onRelease = (up: PointerEvent) => {
      if (up.pointerId !== pointerId) return
      releasesRef.current.get(pointerId)?.()

      // A cancelled gesture (the browser took the pointer for a scroll, a
      // touch was interrupted) is torn down like a release, but it was not a
      // click and must not open the thread.
      if (up.type === 'pointercancel') return

      // A press that travelled was a drag, and must not also open the thread
      // it has just moved. DRAG_SLOP absorbs the pixel or two a normal click
      // carries.
      if (Math.hypot(up.clientX - startX, up.clientY - startY) > DRAG_SLOP) return
      readAnchor()
      setIsPopoverOpen((open) => !open)
    }

    // Capture phase, so a handler further down that stops propagation cannot
    // swallow the release the way the old code swallowed the press.
    window.addEventListener('pointerup', onRelease, true)
    window.addEventListener('pointercancel', onRelease, true)
    releasesRef.current.set(pointerId, () => {
      window.removeEventListener('pointerup', onRelease, true)
      window.removeEventListener('pointercancel', onRelease, true)
      releasesRef.current.delete(pointerId)
    })
  }

  // Handle right-click context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (commentId) {
      setContextMenu({ x: e.clientX, y: e.clientY })
    }
  }

  // Handle delete from context menu
  const handleDeleteComment = async () => {
    if (!commentId) return
    try {
      await deleteComment(commentId)
      setContextMenu(null)
    } catch (error) {
      console.error('Failed to delete comment:', error)
    }
  }

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return
    const handleClick = (e: MouseEvent) => {
      // Don't close if clicking inside the context menu itself
      if (contextMenuRef.current?.contains(e.target as Node)) return
      setContextMenu(null)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [contextMenu])

  // Get background color based on state
  const getBackgroundColor = () => {
    if (resolved) return colors.gray[200]
    if (unreadCount > 0) return colors.blue[500]
    return colors.orange[500]
  }

  // Get border color
  const getBorderColor = () => {
    if (resolved) return colors.gray[300]
    if (unreadCount > 0) return colors.blue[600]
    return colors.orange[600]
  }

  return (
    <>
      <div
        ref={pinRef}
        onPointerDown={handlePinPointerDown}
        onContextMenu={handleContextMenu}
        className="relative w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing transition-all hover:brightness-90"
        style={{
          backgroundColor: getBackgroundColor(),
          borderRadius: '50%',
          border: `2px solid ${getBorderColor()}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        {/* Main icon */}
        <div className="relative">
          <MessageSquare
            size={20}
            className={cn(
              resolved ? 'text-gray-600 dark:text-gray-400 fill-gray-400 dark:fill-gray-500' : 'text-white fill-white',
            )}
          />

          {/* Resolved checkmark overlay */}
          {resolved && (
            <CheckCircle
              size={12}
              className="absolute -bottom-1 -right-1"
              style={{
                color: colors.success[500],
                backgroundColor: 'hsl(var(--card))',
                borderRadius: '50%',
              }}
            />
          )}
        </div>

        {/* Unread badge */}
        {unreadCount > 0 && !resolved && (
          <div
            className="absolute -top-1 -right-1 rounded-full text-xs font-bold text-white flex items-center justify-center"
            style={{
              backgroundColor: colors.error[500],
              minWidth: '18px',
              height: '18px',
              padding: '0 4px',
              fontSize: '10px',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}

      </div>

      {/* Right-click context menu - rendered via Portal */}
      {contextMenu && commentId && createPortal(
        <div
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10001,
          }}
          className="bg-white dark:bg-dark-card rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-1 min-w-[140px]"
        >
          {isOwnComment ? (
            <button
              type="button"
              onPointerDown={(e: React.PointerEvent) => {
                e.preventDefault()
                e.stopPropagation()
                handleDeleteComment()
              }}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 size={14} />
              {t('board:comments.delete_comment', { defaultValue: 'Delete comment' })}
            </button>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
              {t('board:comments.delete_own_only', { defaultValue: 'You can only delete your own comments' })}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Thread popover - rendered via Portal to escape shape bounds */}
      {isPopoverOpen && commentId && screenPosition && createPortal(
        <CommentThreadPopover
          commentId={commentId}
          isOpen={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          anchorX={screenPosition.x}
          anchorY={screenPosition.y}
        />,
        document.body
      )}
    </>
  )
}

export default CommentPin